import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { TONE_BADGE, TONE_TEXT } from '../utils/statusColors';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
  FilterFn,
} from "@tanstack/react-table";
import { fmtPhone } from "../utils/formatters";
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaId, CelulaCliente,
  CelulaDinheiro, CelulaStatus, CelulaAcoes, PaginacaoDaLista,
} from "./shared/lista/PecasDaLista";
import ListToolbar, { ToolbarSearch } from "./shared/ListToolbar";
import { EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, Carga } from '../design';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n)
    ? "0,00"
    : n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

interface ClientsTableProps {
  /**
   * O termo da busca global (barra do topo) ou do "ver DDRs" do cliente (15/10/2026): a tela abre já filtrada por ele.
   * Vinha para o `search` do App, que esta tela não lê (a busca dela é própria). Mesmo padrão de Faturas e Despesas.
   */
  initialSearch?: string;
  onSearchConsumed?: () => void;
  clients: any[];
  contracts: any[];
  clientDDRs: any[];
  canEdit: boolean;
  /** Separado do canEdit: no backend editor cria e edita cliente, mas só admin apaga. */
  canDelete?: boolean;
  canViewFinancials: boolean;
  canViewFinancialTotals: boolean;
  loading: boolean;
  /**
   * A carga de onde `clients` e `contracts` vêm (a principal do App). Sem ela, com a API fora, a lista dizia "Total
   * Clientes 0", "MRR Total R$ 0,00", "Nenhum cliente encontrado", o card de retenção "nada pendente", e oferecia
   * "Novo Cliente" (08/10/2026, Fase 2 tela 3).
   */
  carga: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm'>;
  /** A carga dos DDRs (a segunda do App): sem ela, "DDRs não carregaram" e "cliente sem DDR" eram a mesma coluna. */
  cargaDosDdrs?: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm'>;
  onTentarDeNovo: () => void;
  onEdit: (client: any) => void;
  onDelete: (client: any) => void;
  /** Inativar pela linha (D2): o App lê as dependências no servidor e confirma antes. */
  onInativar?: (client: any) => void;
  onNavigateDDRs: (name: string) => void;
  Icon: React.FC<{ name: string; className?: string }>;
}

const globalFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  const s = (filterValue as string).toLowerCase().trim();
  if (!s) return true;
  const c = row.original;
  const sDigits = s.replace(/\D/g, "");
  return (
    (c.name || "").toLowerCase().includes(s) ||
    (c.tradeName || "").toLowerCase().includes(s) ||
    (c.companyName || "").toLowerCase().includes(s) ||
    (sDigits.length >= 3 &&
      (c.cnpj || "").replace(/\D/g, "").includes(sDigits)) ||
    // Filial de grupo EXPANDIDO passa sempre. Quem abriu o grupo quer ver o
    // que tem dentro, e o nome da filial pode não ter nada a ver com o da
    // matriz: as do PARANAFRIGOR se chamam PRODUTIVA, REDEFRIGOR, TECH-FIN e
    // TOTALFRIGOR, e o filtro de texto as descartava antes do agrupamento.
    c._grupoAberto === true ||
    (c._matrizNome || "").toLowerCase().includes(s)
  );
};

const statusFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  if (!filterValue || filterValue === "Todos") return true;
  return row.original.status === filterValue;
};

const columnHelper = createColumnHelper<any>();


const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  contracts,
  clientDDRs,
  canEdit,
  canDelete = canEdit,
  canViewFinancials,
  canViewFinancialTotals,
  loading,
  carga,
  cargaDosDdrs,
  onTentarDeNovo,
  onEdit,
  onDelete,
  onInativar,
  onNavigateDDRs,
  Icon,
  initialSearch,
  onSearchConsumed,
}) => {
  // Número sem fonte é "—", sem frase: "0 · nada pendente" com a API fora era alarme afirmando tudo-bem sem ter olhado.
  const temFonte = carga.atualizadoEm !== null;
  const semFonte = textos.numeroSemFonte;
  const ddrsNaoCarregaram = cargaDosDdrs?.estado === 'erro';
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);
  const [globalFilter, setGlobalFilter] = useState(initialSearch || '');
  useEffect(() => {
    if (initialSearch) { setGlobalFilter(initialSearch); onSearchConsumed?.(); }
  }, [initialSearch, onSearchConsumed]);
  // Matrizes expandidas. As filiais ficam recolhidas por padrão: as 37 lojas do
  // FESTVAL ocupavam um quinto da listagem e empurravam o resto do alfabeto.
  const [gruposAbertos, setGruposAbertos] = useState<Set<number>>(new Set());
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [statusFilter, setStatusFilter] = useState("Todos");

  const getClientMRR = useMemo(() => {
    return (id: number) =>
      contracts
        .filter((c: any) => c.clientId === id && c.status === "Ativo")
        .reduce((s: number, c: any) => s + (parseFloat(String(c.mrr)) || 0), 0);
  }, [contracts]);

  const getClientContracts = useMemo(() => {
    return (clientId: number) =>
      contracts.filter(
        (ct: any) => ct.clientId === clientId && ct.status === "Ativo",
      ).length;
  }, [contracts]);

  const activeClients = useMemo(
    () => clients.filter((c: any) => c.status === "Ativo").length,
    [clients],
  );

  const totalMRRClients = useMemo(
    () =>
      clients
        .filter((c: any) => c.status === "Ativo")
        .reduce((s: number, c: any) => s + getClientMRR(c.id), 0),
    [clients, getClientMRR],
  );

  // Cards-filtro: Com/Sem assinatura recortam a lista (anel laranja = ativo)
  const [cardFilter, setCardFilter] = useState<
    null | "com" | "sem" | "retencao" | "faltaretencao"
  >(null);
  const hasActiveContract = useCallback(
    (clientId: number) =>
      contracts.some(
        (ct: any) => ct.clientId === clientId && ct.status === "Ativo",
      ),
    [contracts],
  );
  // Filial é coberta pela assinatura da MATRIZ: não entra em "Sem Assinatura"
  const hasCoverage = useCallback(
    (c: any) =>
      hasActiveContract(c.id) ||
      (c.parentClientId && hasActiveContract(c.parentClientId)),
    [hasActiveContract],
  );
  // `s3HasAudio` é cacheado no cadastro porque descobrir isso na hora custaria
  // uma chamada à AWS por cliente. null = nunca verificado.
  const comRetencao = useMemo(
    () =>
      clients.filter((c: any) => c.status === "Ativo" && c.s3RetentionEnabled)
        .length,
    [clients],
  );
  const precisam = useMemo(
    () =>
      clients.filter((c: any) => c.status === "Ativo" && precisaRetencao(c))
        .length,
    [clients],
  );
  /** Quem tem gravação, não está marcado como "não se aplica" e ainda não foi
   *  configurado. É a fila de trabalho do rollout.
   *
   *  Vem do CADASTRO, não da AWS: conferir regra por regra custaria uma chamada
   *  por cliente a cada abertura da tela. Cliente com a flag ligada mas sem
   *  regra gravada (bucket errado, por exemplo) não aparece aqui. */
  const faltaConfigurar = useMemo(
    () =>
      (clients as any[]).filter(
        (c) =>
          c.status === "Ativo" && precisaRetencao(c) && !c.s3RetentionEnabled,
      ),
    [clients],
  );

  /** Cliente por id, para a empresa do grupo poder ler o estado da matriz. */
  const porId = useMemo(() => {
    const m = new Map<number, any>();
    for (const c of clients as any[]) m.set(c.id, c);
    return m;
  }, [clients]);

  /** Quantas empresas do grupo (parentClientId) cada matriz tem. "Filial" fica só
   *  para LOCAL (client_locations); empresa ligada por parentClientId é do grupo. */
  const filiaisPorMatriz = useMemo(() => {
    const m = new Map<number, number>();
    for (const c of clients as any[]) {
      if (c.parentClientId)
        m.set(c.parentClientId, (m.get(c.parentClientId) || 0) + 1);
    }
    return m;
  }, [clients]);

  const cardFilteredClients = useMemo(() => {
    if (cardFilter === "com")
      return clients.filter((c: any) => hasActiveContract(c.id));
    if (cardFilter === "sem")
      return clients.filter(
        (c: any) => c.status === "Ativo" && !hasCoverage(c),
      );
    // Só quem TEM gravação. Boa parte da base é STFC pelo SBC e não tem o que
    // reter: incluir esses inflaria o número e o card perderia o sentido.
    if (cardFilter === "retencao")
      return clients.filter(
        (c: any) =>
          c.status === "Ativo" && precisaRetencao(c) && c.s3RetentionEnabled,
      );
    if (cardFilter === "faltaretencao") return faltaConfigurar;
    return clients;
  }, [clients, cardFilter, hasActiveContract, hasCoverage, faltaConfigurar]);

  /**
   * Esconde as filiais de matriz recolhida. Com busca ativa nada é escondido:
   * procurar "L22" e não achar a loja porque o grupo estava fechado seria pior
   * que a lista plana de antes.
   */
  const clientesVisiveis = useMemo(() => {
    const termo = globalFilter.trim().toLowerCase();
    // Cada filial carrega o nome da matriz para o filtro global poder deixá-la
    // passar quando a busca é pelo grupo.
    const lista = (cardFilteredClients as any[]).map((c) =>
      c.parentClientId
        ? {
            ...c,
            _matrizNome: porId.get(c.parentClientId)?.name || "",
            _grupoAberto: gruposAbertos.has(c.parentClientId),
          }
        : c,
    );

    // Nomes das matrizes que casam com a busca. Procurar "festval" é procurar o
    // GRUPO, e nesse caso o grupo continua recolhido. Procurar "L22" é procurar
    // uma loja, e aí ela precisa aparecer mesmo com o grupo fechado — senão a
    // busca ficaria pior que a lista plana de antes.
    const matrizBuscada = new Set<number>();
    if (termo) {
      for (const c of lista) {
        if (
          filiaisPorMatriz.get(c.id) &&
          `${c.name || ""} ${c.tradeName || ""}`.toLowerCase().includes(termo)
        ) {
          matrizBuscada.add(c.id);
        }
      }
    }

    return lista.filter((c) => {
      if (!c.parentClientId) return true;
      if (gruposAbertos.has(c.parentClientId)) return true;
      // Filial só aparece recolhida se a busca não era pela matriz dela.
      return !!termo && !matrizBuscada.has(c.parentClientId);
    });
  }, [
    cardFilteredClients,
    gruposAbertos,
    globalFilter,
    filiaisPorMatriz,
    porId,
  ]);

  const clientsWithContracts = useMemo(
    () =>
      clients.filter((c: any) =>
        contracts.some(
          (ct: any) => ct.clientId === c.id && ct.status === "Ativo",
        ),
      ).length,
    [clients, contracts],
  );

  const clientsWithoutContracts = useMemo(
    () =>
      clients.filter((c: any) => c.status === "Ativo" && !hasCoverage(c))
        .length,
    [clients, hasCoverage],
  );

  const clientsWithoutFinancialEmail = useMemo(
    () =>
      clients.filter((c: any) => {
        if (c.status !== "Ativo") return false;
        // Quem está com "Envio por Email: Não enviar" não tem e-mail financeiro
        // porque assim foi decidido — o alerta existe para avisar que a fatura
        // NÃO VAI SAIR, e nesses casos ela não sairia de qualquer jeito. Cobrar
        // um cadastro que o próprio cadastro dispensa é o que fazia o aviso
        // virar ruído permanente (caso MCID, que paga por TED).
        if (c.emailDeliveryType === "nenhum") return false;
        const hasFinancialEmail =
          c.emailFinancial && c.emailFinancial.trim() !== "";
        const hasFinancialEmailInList = c.emails?.some(
          (e: any) => e.type === "Financeiro" && e.email?.trim(),
        );
        return !hasFinancialEmail && !hasFinancialEmailInList;
      }),
    [clients],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        id: "id",
        header: "ID",
        // Fase 2 do padrão das listas (22/10/2026): as células são as peças de shared/lista (guia §3c).
        cell: ({ getValue }) => <CelulaId id={getValue()} />,
      }),
      columnHelper.accessor(
        (row) => row.name || row.tradeName || row.companyName,
        {
          id: "name",
          header: "Cliente",
          cell: ({ row }) => {
            const c = row.original;
            const nFiliais = filiaisPorMatriz.get(c.id) || 0;
            const aberto = gruposAbertos.has(c.id);
            // C1 (Heitor 21/10/2026): o CNPJ sai da coluna própria e vai para a sub-linha, junto da razão social e da
            // cidade. A origem do cliente e a IE, que ocupavam outras linhas, ficam no tooltip da sub-linha.
            const detalhe = [
              c.inscricaoEstadual ? `IE ${c.inscricaoEstadual}` : "",
              c.referralSource ? `Origem: ${c.referralSource}${(c as any).referralDetail ? ` (${(c as any).referralDetail})` : ""}` : "",
            ].filter(Boolean).join("\n");
            return (
              <CelulaCliente
                nome={c.name || c.tradeName}
                razao={c.tradeName && c.tradeName !== c.name ? c.tradeName : c.companyName}
                cnpj={c.cnpj}
                cidade={c.city ? `${c.city}${c.state ? `/${c.state}` : ""}` : null}
                detalhe={detalhe || null}
                recuo={!!c.parentClientId}
                antes={nFiliais > 0 && (
                  <button
                    onClick={() =>
                      setGruposAbertos((prev) => {
                        const n = new Set(prev);
                        n.has(c.id) ? n.delete(c.id) : n.add(c.id);
                        return n;
                      })
                    }
                    className="shrink-0 text-slate-400 hover:text-white"
                    title={aberto ? "Recolher as empresas do grupo" : "Expandir as empresas do grupo"}
                  >
                    {aberto ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                selos={<>
                  {nFiliais > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded whitespace-nowrap ${TONE_BADGE.group}`}>
                      {nFiliais} no grupo
                    </span>
                  )}
                  {c.parentClientId && (
                    <span className={`px-1.5 py-0.5 text-xs rounded whitespace-nowrap ${TONE_BADGE.group}`}>
                      do grupo
                    </span>
                  )}
                  {c.isPublicSector && (
                    // Setor público é categoria, não alerta: `info`, não warning.
                    <span className={`px-1.5 py-0.5 text-xs rounded whitespace-nowrap ${TONE_BADGE.info}`}>
                      Pub.
                    </span>
                  )}
                </>}
              />
            );
          },
          filterFn: globalFilterFn,
        },
      ),
      columnHelper.display({
        id: "contact",
        header: "Contato",
        cell: ({ row }) => {
          const c = row.original;
          const hasFinancialEmail =
            (c.emailFinancial && c.emailFinancial.trim() !== "") ||
            c.emails?.some(
              (e: any) => e.type === "Financeiro" && e.email?.trim(),
            );
          return (
            <td className={`${CELULA}`}>
              <p className="text-sm text-white whitespace-nowrap">
                {fmtPhone((c.phones?.[0]?.phone || "").split("/")[0].trim())}
              </p>
              <div className="flex items-center gap-1">
                <p className="text-xs text-slate-500 truncate">
                  {c.emails?.[0]?.email || c.emailFinancial || (
                    <span className={TONE_TEXT.warning}>Sem email</span>
                  )}
                </p>
                {c.status === "Ativo" && !hasFinancialEmail && (
                  <span
                    title="Sem email financeiro"
                    className={TONE_TEXT.warning}
                  >
                    <Icon name="alertCircle" className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            </td>
          );
        },
      }),
      columnHelper.display({
        id: "cobranca",
        header: "Cobrança",
        cell: ({ row }) => {
          const c = row.original;
          const pm: Record<string, { label: string; cls: string }> = {
            // Forma de cobrança é categoria. O laranja que marcava TED é o acento da
            // ação primária (§8.1), por isso TED passa a `info`.
            boleto: { label: "Boleto", cls: TONE_BADGE.progress },
            pix: { label: "PIX", cls: TONE_BADGE.open },
            ted: { label: "TED", cls: TONE_BADGE.info },
            deposito: { label: "Dep.", cls: "bg-slate-600 text-slate-300" },
          };
          const envio: Record<string, string> = {
            nf_fatura_boleto: "NF+Fat+Bol",
            nf_fatura: "NF+Fat",
            nf_fatura_relatorio: "NF+Fat+Rel",
            nf_fatura_boleto_relatorio: "NF+Fat+Rel+Bol",
            nenhum: "Sem envio",
          };
          const m = pm[c.paymentMethod];
          const venc = c.paymentTermDays
            ? `${c.paymentTermDays}d`
            : c.dueDay
              ? `dia ${c.dueDay}`
              : null;
          return (
            <td className={`${CELULA} text-center whitespace-nowrap`}>
              <div className="flex items-center justify-center gap-1.5">
                {m ? (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}
                  >
                    {m.label}
                  </span>
                ) : (
                  <span className="text-slate-600 text-xs">{"\u2014"}</span>
                )}
                {venc && (
                  <span
                    className="text-xs text-slate-400"
                    title={
                      c.paymentTermDays
                        ? `Prazo: emissão + ${c.paymentTermDays} dias`
                        : "Dia de vencimento"
                    }
                  >
                    {venc}
                  </span>
                )}
              </div>
              {c.emailDeliveryType && (
                <p
                  className="text-[11px] text-slate-500 mt-0.5"
                  title="O que este cliente recebe por email"
                >
                  {envio[c.emailDeliveryType] || c.emailDeliveryType}
                </p>
              )}
            </td>
          );
        },
      }),
      columnHelper.accessor((row) => getClientContracts(row.id), {
        id: "vinculos",
        header: "Vínculos",
        cell: ({ row, getValue }) => {
          const numContracts = getValue();
          const c = row.original;
          const numDDRs = clientDDRs.filter(
            (d: any) => d.clientId === c.id,
          ).length;
          return (
            <td className={`${CELULA} text-center`}>
              <div className="flex flex-col items-center gap-1">
                {numContracts > 0 ? (
                  <span className={`${TONE_BADGE.info} px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap`}>
                    {numContracts} assin.
                  </span>
                ) : (
                  <span className="text-slate-600 text-xs">{"\u2014"}</span>
                )}
                {ddrsNaoCarregaram ? (
                  <span
                    className="text-slate-500 text-xs whitespace-nowrap"
                    title={`Não sei se tem DDRs: a lista de DDRs não carregou (${cargaDosDdrs?.erro})`}
                    data-ddrs="nao-carregou"
                  >
                    DDRs {semFonte}
                  </span>
                ) : numDDRs > 0 && (
                  <button
                    onClick={() => onNavigateDDRs(c.name || c.tradeName)}
                    className={`${TONE_BADGE.progress} px-2 py-0.5 rounded-full text-xs font-medium hover:brightness-125 transition whitespace-nowrap`}
                  >
                    {numDDRs} DDR{numDDRs !== 1 ? "s" : ""}
                  </button>
                )}
              </div>
            </td>
          );
        },
      }),
      columnHelper.display({
        id: "retencao",
        header: "Retenção",
        cell: ({ row }) => {
          const c: any = row.original;
          const rules: Array<{ prefix: string; retentionDays: number }> =
            c.s3RetentionRules || [];
          const temBucket = !!(c.s3BucketName || c.ec2InstanceName);

          // A coluna mostra o CADASTRO, não a AWS: consultar a AWS por cliente
          // custaria 194 chamadas a cada abertura da lista. O estado real de cada
          // bucket, e a divergência entre os dois, ficam no modal do cliente.

          // Filial herda a matriz: mesmo sistema, mesmo bucket. Mostrar
          // "sem retenção" nelas era falso e criava 41 pendências fantasma.
          if (c.parentClientId) {
            const matriz = porId.get(c.parentClientId);
            const prazos = Array.from(
              new Set(
                ((matriz?.s3RetentionRules || []) as any[])
                  .filter((r) => AUDIO_PREFIXES.includes(r.prefix))
                  .map((r) => r.retentionDays),
              ),
            ).sort((a: any, b: any) => a - b);
            const rotulo = matriz?.s3RetentionNotApplicable
              ? "não se aplica"
              : matriz?.s3RetentionEnabled && prazos.length
                ? prazos.map((d: any) => anosOuDias(d)).join(" / ")
                : "—";
            return (
              <td className={`${CELULA} text-center`}>
                <span
                  className="text-slate-500 text-xs whitespace-nowrap"
                  title={`Herdado da matriz ${matriz?.name || ""}: mesmo sistema e mesmo bucket`}
                >
                  {rotulo}
                </span>
              </td>
            );
          }

          // "Não se aplica" vem PRIMEIRO, antes de qualquer coisa. É uma decisão
          // registrada, e a decisão vale mesmo para quem não tem bucket nenhum —
          // aliás, é justamente o caso mais comum: cliente que saiu da nuvem não
          // tem instância, e a checagem de bucket engolia a marcação dele.
          if (!precisaRetencao(c)) {
            return (
              <td className={`${CELULA} text-center`}>
                <span
                  className="text-slate-500 text-xs whitespace-nowrap"
                  title={
                    c.s3RetentionNotApplicableReason ||
                    "Marcado como sem gravação em nuvem"
                  }
                >
                  não se aplica
                </span>
              </td>
            );
          }

          // Sem bucket nem instância não há o que reter. O tooltip diz o
          // porquê, para o travessão não virar lacuna sem explicação.
          if (!temBucket) {
            return (
              <td className={`${CELULA} text-center`}>
                <span
                  className="text-slate-600 text-xs"
                  title={
                    c.hasAwsInfra === false
                      ? "Cliente sem infraestrutura AWS"
                      : "Sem bucket nem instância cadastrada"
                  }
                >
                  {"\u2014"}
                </span>
              </td>
            );
          }
          if (!c.s3RetentionEnabled) {
            // O destaque só vale para quem TEM gravação. Cliente de STFC pelo
            // SBC não tem o que reter, e marcá-lo como pendente transformaria a
            // coluna em ruído: seriam 155 avisos para uma fila real de 59.
            return (
              <td className={`${CELULA} text-center`}>
                {/* AUSÊNCIA, não aviso (Fase 2 tela 3, 08/10/2026): era um selo âmbar em quase todas as linhas, o estado
                    normal da casa pintado de alerta, e o ruído escondia o que importa. Quem carrega peso é o selo de
                    quem TEM retenção; a fila de quem falta está no card "A Configurar". */}
                <span
                  className="text-central-texto-fraco text-xs whitespace-nowrap"
                  title="Tem gravação e nenhuma retenção: o acervo cresce sem limite"
                  data-retencao="sem"
                >
                  sem retenção
                </span>
              </td>
            );
          }
          // A coluna é sobre retenção de ÁUDIO, que é o nome da seção. Pastas
          // como `Backup-Snep/` são exceções configuradas pelo mesmo mecanismo
          // e não dizem nada sobre o acervo de gravação: a CARTSYS tem 5 anos
          // de áudio e 1 ano de backup, e mostrar "1 ano / 5 anos" misturava as
          // duas coisas. Sem nenhuma pasta de áudio conhecida, cai para todas,
          // para nunca esconder que existe retenção valendo.
          const deAudio = rules.filter((r) =>
            AUDIO_PREFIXES.includes(r.prefix),
          );
          const consideradas = deAudio.length ? deAudio : rules;
          const prazos = Array.from(
            new Set(consideradas.map((r) => r.retentionDays)),
          ).sort((a, b) => a - b);
          const label = prazos.length
            ? prazos.map((d) => anosOuDias(d)).join(" / ")
            : "—";
          // D3 (12/10/2026): "1 ano" verde em todo cliente não dizia nada. O padrão (até 1 ano) fica neutro; a cor marca
          // só a exceção, retenção acima de 1 ano.
          const excecao = prazos.length > 0 && Math.max(...prazos) > 365;
          return (
            <td className={`${CELULA} text-center`}>
              <span
                className={`${excecao ? TONE_BADGE.success : TONE_BADGE.neutral} px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap`}
                data-retencao={excecao ? 'excecao' : 'padrao'}
                title={rules
                  .map((r) => `${r.prefix} ${r.retentionDays} dias`)
                  .join("\n")}
              >
                {label}
              </span>
            </td>
          );
        },
      }),
      columnHelper.accessor((row) => getClientMRR(row.id), {
        id: "mrr",
        header: "MRR",
        cell: ({ getValue }) => {
          const mrr = getValue();
          // E1: branco com peso (CelulaDinheiro); sem MRR, "—".
          return <CelulaDinheiro valor={mrr > 0 ? mrr : null} />;
        },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return <CelulaStatus texto={status} tom={status === "Ativo" ? "neutral" : "danger"} />;
        },
        filterFn: statusFilterFn,
      }),
      columnHelper.display({
        id: "actions",
        header: "Ações",
        cell: ({ row }) => {
          const c = row.original;
          // D1: editar + ⋯; Inativar e Excluir no ⋯ (Excluir em perigo, separado por traço), os dois confirmam.
          return (
            <CelulaAcoes
              rotuloDoMenu="Mais ações do cliente"
              onEditar={() => onEdit(c)}
              Icon={Icon}
              itens={[
                ...(onInativar && c.status === 'Ativo' ? [{ label: 'Inativar', onClick: () => onInativar(c) }] : []),
                ...(canDelete ? [{ label: 'Excluir', onClick: () => onDelete(c), danger: true, separadoAntes: !!onInativar && c.status === 'Ativo' }] : []),
              ]}
            />
          );
        },
      }),
    ],
    [
      getClientMRR,
      getClientContracts,
      clientDDRs,
      onEdit,
      onDelete,
      onInativar,
      onNavigateDDRs,
      Icon,
    ],
  );

  const columnVisibility = useMemo(
    () => ({
      mrr: canViewFinancials,
      actions: canEdit,
    }),
    [canViewFinancials, canEdit],
  );

  const table = useReactTable({
    data: clientesVisiveis,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Expandir ou recolher um grupo muda o array de dados, e por padrão o
    // TanStack joga a paginação de volta para a página 1. Quem clicou na seta
    // da 3ª página não quer ser mandado para o começo da lista.
    autoResetPageIndex: false,
    initialState: {
      pagination: { pageSize: 50 },
    },
  });

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    const statusColumn = table.getColumn("status");
    if (statusColumn) {
      statusColumn.setFilterValue(status === "Todos" ? undefined : status);
    }
  };

  // `autoResetPageIndex` está desligado para expandir/recolher não jogar de
  // volta para a página 1. Mas a BUSCA precisa resetar: filtrar estando na
  // página 2 deixava a lista vazia, parecendo que nada foi encontrado.
  useEffect(() => {
    table.setPageIndex(0);
  }, [globalFilter, table]);

  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const filiaisRecolhidas =
    !globalFilter.trim() &&
    (cardFilteredClients as any[]).filter(
      (c) => c.parentClientId && !gruposAbertos.has(c.parentClientId),
    ).length;

  return (
    <div className="space-y-4">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <button
          onClick={() => setCardFilter(null)}
          className={`bg-central-cartao rounded-central p-4 border text-left transition-all ${cardFilter === null ? "border-central-borda border-l-[3px] border-l-primary bg-central-elevado" : "border-central-borda hover:bg-central-elevado"}`}
        >
          <p className="text-sm text-slate-400">Total Clientes</p>
          <p className="text-2xl font-bold whitespace-nowrap text-white mt-1" data-kpi="total">
            {temFonte ? clients.length : semFonte}
          </p>
          {temFonte && <p className="text-slate-500 text-xs mt-1">{activeClients} ativos</p>}
        </button>
        {canViewFinancialTotals && (
          <div className="bg-central-cartao rounded-central p-4 border border-central-borda">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">MRR Total</p>
                <p className={`text-2xl font-bold whitespace-nowrap mt-1 ${temFonte ? 'text-central-texto' : 'text-central-texto-fraco'}`} data-kpi="mrr">
                  {temFonte ? `R$ ${fmt(totalMRRClients)}` : semFonte}
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Receita recorrente
                </p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={() => setCardFilter(cardFilter === "com" ? null : "com")}
          className={`bg-central-cartao rounded-central p-4 border text-left transition-all ${cardFilter === "com" ? "border-central-borda border-l-[3px] border-l-primary bg-central-elevado" : "border-central-borda hover:bg-central-elevado"}`}
        >
          <p className="text-sm text-slate-400">Com Assinatura</p>
          <p className="text-2xl font-bold whitespace-nowrap mt-1 text-white" data-kpi="com">
            {temFonte ? clientsWithContracts : semFonte}
          </p>
          <p className="text-slate-500 text-xs mt-1">Assinaturas ativas</p>
        </button>
        <button
          onClick={() => setCardFilter(cardFilter === "sem" ? null : "sem")}
          className={`bg-central-cartao rounded-central p-4 border text-left transition-all ${cardFilter === "sem" ? "border-central-borda border-l-[3px] border-l-primary bg-central-elevado" : "border-central-borda hover:bg-central-elevado"}`}
        >
          <p className="text-sm text-slate-400">Sem Assinatura</p>
          <p className="text-2xl font-bold whitespace-nowrap text-white mt-1" data-kpi="sem">
            {temFonte ? clientsWithoutContracts : semFonte}
          </p>
          <p className="text-slate-500 text-xs mt-1">Oportunidade</p>
        </button>
        <button
          onClick={() =>
            setCardFilter(cardFilter === "retencao" ? null : "retencao")
          }
          className={`bg-central-cartao rounded-central p-4 border text-left transition-all ${cardFilter === "retencao" ? "border-central-borda border-l-[3px] border-l-primary bg-central-elevado" : "border-central-borda hover:bg-central-elevado"}`}
        >
          <p className="text-sm text-slate-400">Com Retenção</p>
          <p className="text-2xl font-bold whitespace-nowrap text-white mt-1" data-kpi="retencao">
            {temFonte ? comRetencao : semFonte}
            {temFonte && (
              <span className="text-slate-500 text-lg font-normal">
                {" "}
                / {precisam}
              </span>
            )}
          </p>
          {temFonte && (
            <p className="text-slate-500 text-xs mt-1">
              {comRetencao === 1 ? "cliente configurado" : "clientes configurados"}
            </p>
          )}
        </button>
        <button
          onClick={() =>
            setCardFilter(
              cardFilter === "faltaretencao" ? null : "faltaretencao",
            )
          }
          disabled={faltaConfigurar.length === 0}
          title="Clientes com gravação que ainda não têm retenção configurada"
          className={`bg-central-cartao rounded-central p-4 border text-left transition-all disabled:cursor-default ${cardFilter === "faltaretencao" ? "border-central-borda border-l-[3px] border-l-primary bg-central-elevado" : "border-central-borda hover:bg-central-elevado"}`}
        >
          <p className="text-sm text-slate-400">{temFonte && faltaConfigurar.length > 0 && <span aria-hidden>⚠ </span>}A Configurar</p>
          <p
            className={`text-2xl whitespace-nowrap mt-1 text-white ${temFonte && faltaConfigurar.length > 0 ? "font-bold" : "font-normal"}`}
            data-kpi="configurar"
          >
            {temFonte ? faltaConfigurar.length : semFonte}
          </p>
          {temFonte && (
            <p className="text-slate-500 text-xs mt-1">
              {faltaConfigurar.length > 0
                ? "clique para filtrar"
                : "nada pendente"}
            </p>
          )}
        </button>
      </div>

      {/* Alerta: clientes sem e-mail financeiro.
          Uma linha, não um cartão: é aviso permanente (quase sempre há algum) e
          ocupava um oitavo da tela acima da lista que a pessoa veio ver. O texto
          diz a consequência, que é o que importa — o resto vira o próprio chip,
          que já leva ao cadastro. */}
      {clientsWithoutFinancialEmail.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-amber-500/10 border border-amber-600/40 rounded-lg px-3 py-2">
          <Icon name="mail" className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-sm text-amber-200/90">
            <span className="font-semibold text-amber-300">{clientsWithoutFinancialEmail.length}</span>{" "}
            {clientsWithoutFinancialEmail.length === 1 ? "cliente ativo" : "clientes ativos"} sem e-mail
            financeiro — não recebem fatura nem NF
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {clientsWithoutFinancialEmail.slice(0, 5).map((c: any) => (
              <button
                key={c.id}
                onClick={() => onEdit(c)}
                title="Abrir cadastro para incluir o e-mail"
                className={`px-2 py-0.5 ${TONE_BADGE.warning} text-xs rounded hover:brightness-125 transition`}
              >
                {c.name || c.tradeName || c.companyName}
              </button>
            ))}
            {clientsWithoutFinancialEmail.length > 5 && (
              <span className="text-amber-300/70 text-xs">
                +{clientsWithoutFinancialEmail.length - 5}
              </span>
            )}
          </div>
        </div>
      )}

      <ListToolbar
        search={
          <ToolbarSearch
            value={globalFilter}
            onChange={setGlobalFilter}
            placeholder="Buscar por nome, CNPJ, email..."
          />
        }
        filters={
          <div className="flex gap-2">
            {["Todos", "Ativo", "Inativo"].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === status ? "bg-slate-600 text-white font-semibold" : "bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700"}`}
              >
                {status}{" "}
                {status !== "Todos" && (
                  <span className="ml-1 text-xs">
                    ({temFonte ? clients.filter((c: any) => c.status === status).length : semFonte})
                  </span>
                )}
              </button>
            ))}
          </div>
        }
        actions={
          // "Novo Cliente" só com a carga PRONTA: com a lista sem chegar, criar é criar sem ver se o cliente já existe.
          canEdit && carga.estado === 'pronto' && (
            <button
              onClick={() => onEdit(null)}
              className="flex items-center gap-2 px-4 py-2.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg font-medium whitespace-nowrap"
            >
              <Icon name="plus" className="w-5 h-5" />
              Novo Cliente
            </button>
          )
        }
      />

      {/* Tabela */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Carregando...</div>
      ) : (
        <EstadoDaLista
          carga={carga}
          temDados={temFonte}
          oQue="os clientes"
          onTentarDeNovo={onTentarDeNovo}
          vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}
        >
        <CascaDaLista>
            <TabelaDaLista>
              {/* Cabeçalho fixo (CabecalhoDaLista: -top-6 desconta o padding do <main>). Cliente fica sem largura e herda
                  o resto (table-fixed); a coluna CNPJ saiu (C1). */}
              <CabecalhoDaLista>
                {table.getHeaderGroups()[0].headers.map((header) => {
                  const larguras: Record<string, string> = {
                    id: "w-14",
                    contact: "w-56",
                    cobranca: "w-40",
                    vinculos: "w-24",
                    retencao: "w-28",
                    mrr: "w-32",
                    status: "w-24",
                    actions: "w-24",
                  };
                  const alinhamento = ["mrr", "actions"].includes(header.id)
                    ? "direita"
                    : ["cobranca", "vinculos", "retencao", "status"].includes(header.id)
                      ? "centro"
                      : "esquerda";
                  return (
                    <ColunaDaLista
                      key={header.id}
                      rotulo={flexRender(header.column.columnDef.header, header.getContext())}
                      largura={larguras[header.id] || ""}
                      alinhamento={alinhamento}
                      ordem={header.column.getCanSort() ? header.column.getIsSorted() : undefined}
                      onOrdenar={() => header.column.toggleSorting()}
                    />
                  );
                })}
              </CabecalhoDaLista>
              <tbody className="divide-y divide-slate-800">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumnCount}
                      className="px-4 py-12 text-center text-slate-500"
                    >
                      Nenhum cliente encontrado
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <LinhaDaLista key={row.id}>
                      {row
                        .getVisibleCells()
                        .map((cell) =>
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          ),
                        )}
                    </LinhaDaLista>
                  ))
                )}
                {/* Diz quantas linhas estão escondidas, para o total da lista
                    não parecer ter encolhido sozinho. */}
                {filiaisRecolhidas > 0 && (
                  <tr>
                    <td
                      colSpan={visibleColumnCount}
                      className="px-4 py-3 text-center text-xs text-slate-500"
                    >
                      {filiaisRecolhidas} empresas de grupo recolhidas sob suas matrizes.
                      Clique na seta ao lado do nome para expandir.
                    </td>
                  </tr>
                )}
              </tbody>
            </TabelaDaLista>

          {/* Paginação (PaginacaoDaLista): o recorte, o tamanho da página com "Todos", e os números. */}
          <PaginacaoDaLista
            pagina={table.getState().pagination.pageIndex + 1}
            tamanho={table.getState().pagination.pageSize}
            total={table.getFilteredRowModel().rows.length}
            onPagina={(p) => table.setPageIndex(p - 1)}
            onTamanho={(t) => table.setPageSize(t)}
            tamanhos={[25, 50, 100]}
            todos
          />
        </CascaDaLista>
        </EstadoDaLista>
      )}
    </div>
  );
};

/**
 * O cliente precisa de retenção? Tudo que não foi marcado como "não se aplica"
 * precisa, até alguém dizer o contrário. A detecção automática (`s3HasAudio`)
 * não serve de porteiro: ela não distingue "não consegui verificar" de "não
 * tem", e o bucket com nome errado cai no mesmo balde de quem saiu da nuvem.
 */
function precisaRetencao(c: any): boolean {
  // Filial não é uma decisão separada: as 37 lojas do FESTVAL compartilham o
  // mesmo sistema e o mesmo bucket da matriz. Contá-las como pendentes
  // inflaria a fila em 41 itens que representam 2 decisões.
  if (c.parentClientId) return false;
  return !c.s3RetentionNotApplicable;
}

/** Pastas de gravação: `monitor/` é o formato atual e `storage1/` o antigo. */
const AUDIO_PREFIXES = ["monitor/", "storage1/"];

/** 730 -> "2 anos", 90 -> "90d". Prazo de retenção é sempre lido em anos na conversa. */
function anosOuDias(dias: number): string {
  if (dias % 365 === 0) {
    const anos = dias / 365;
    return `${anos} ano${anos > 1 ? "s" : ""}`;
  }
  return `${dias}d`;
}

export default ClientsTable;
